import React, { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";

import { api } from "../lib/api";
import { AppError, toUserMessage } from "../lib/errors";
import { hasRequiredProfileValues } from "../lib/profileFlow";
import { storage } from "../lib/storage";

type GateState =
  | { kind: "checking" }
  | { kind: "complete" }
  | { kind: "incomplete" }
  | { kind: "unauthenticated" }
  | { kind: "error"; message: string };

type ProfileCompletionGateProps = {
  children: React.ReactElement;
};

function hasStoredSession(): boolean {
  const auth = storage.getAuth();
  return Boolean(auth.userId || auth.accessToken || auth.refreshToken || auth.secretKey);
}

export default function ProfileCompletionGate({ children }: ProfileCompletionGateProps) {
  const location = useLocation();
  const [state, setState] = useState<GateState>({ kind: "checking" });

  useEffect(() => {
    let active = true;

    if (!hasStoredSession()) {
      setState({ kind: "unauthenticated" });
      return () => {
        active = false;
      };
    }

    setState({ kind: "checking" });

    (async () => {
      try {
        const profiles = await api.getUserProfile();
        if (!active) return;
        setState(hasRequiredProfileValues(profiles) ? { kind: "complete" } : { kind: "incomplete" });
      } catch (error) {
        if (!active) return;
        if (error instanceof AppError && error.code === "SESSION_EXPIRED") {
          setState({ kind: "unauthenticated" });
          return;
        }
        setState({ kind: "error", message: toUserMessage(error) });
      }
    })();

    return () => {
      active = false;
    };
  }, [location.pathname, location.search]);

  if (state.kind === "complete") {
    return children;
  }

  if (state.kind === "incomplete") {
    const returnTo = `${location.pathname}${location.search}`;
    return <Navigate to="/onboarding" replace state={{ returnTo }} />;
  }

  if (state.kind === "unauthenticated") {
    return <Navigate to="/splash" replace />;
  }

  return (
    <main className="flex min-h-screen items-center justify-center bg-[#FDF8F5] px-6 text-[#4A3E39]">
      <section className="w-full max-w-md rounded-[2rem] border border-[#EADBC8] bg-white/90 p-6 shadow-[0_18px_40px_rgba(124,90,78,0.14)]">
        {state.kind === "checking" ? (
          <p className="text-sm font-bold text-[#7B6861]">プロファイルを確認しています...</p>
        ) : (
          <>
            <h1 className="text-lg font-black text-[#7C5A4E]">プロファイルを確認できませんでした</h1>
            <p className="mt-2 text-sm leading-6 text-[#A1493E]" role="alert">
              {state.message}
            </p>
          </>
        )}
      </section>
    </main>
  );
}
